import { BiSolidCheckCircle, BiSolidTag } from "solid-icons/bi";
import { type Component, createMemo, For, type JSX, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { cn } from "@/libs/cn";
import type { BillingInterval, Plan } from "../api/types";
import { computePlanPricing } from "../utils/savings";
import { getCheckoutTrialInfo } from "../utils/trial";

export type PlanCardCta = {
  label: string;
  onClick?: () => void;
  variant?: "default" | "outline" | "neutralOutline";
  disabled?: boolean;
};

type PlanCardProps = {
  plan: Plan;
  interval: BillingInterval;
  /** Promo discount for this plan/user (from the campaign endpoint). null = standard pricing. */
  discountPercent?: number | null;
  /** Show the "prezzo bloccato a vita" note alongside an active promo. */
  lockedForLife?: boolean;
  /** "Più scelto" badge + primary accent (Headhunter). */
  recommended?: boolean;
  /** "Il tuo piano attuale" badge + ring (in-app upgrade page). */
  currentBadge?: boolean;
  /** Fully-resolved call to action, computed by the parent (sales vs manage logic). */
  cta: PlanCardCta;
  /** Override the feature bullets (design-curated sales copy). Falls back to backend-derived lines. */
  features?: string[];
  /** Small note under the CTA (e.g. the trial line). */
  ctaNote?: string;
  /**
   * Render the free-trial line under the CTA (interval-aware, price-aware).
   * Only for prospects who would start a trial — hide it for existing subscribers.
   * Takes precedence over `ctaNote` when a trial applies.
   */
  showTrialNote?: boolean;
  /** Icon rendered in the top-right circle. */
  icon?: JSX.Element;
  /** Secondary line under "/mese" (e.g. Team "per tutto il team"). */
  priceSubtext?: string;
  /** Highlighted tag under the price (e.g. Team "prezzo unico per il team"). */
  tag?: string;
};

/** Italian money formatting: integers plain, decimals with a comma (24.5 → "24,50"). */
const formatMoney = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(2).replace(".", ",");

export const PlanCard: Component<PlanCardProps> = (props) => {
  const { t } = useI18n();

  const isContactSales = () => !!props.plan.features?.requires_contact_sales;

  const pricing = createMemo(() =>
    computePlanPricing({
      priceMonthly: props.plan.price_monthly,
      priceYearly: props.plan.price_yearly,
      cycle: props.interval,
      discountPercent: props.discountPercent ?? null,
    }),
  );

  const hasPromo = () => !!props.discountPercent && !!props.lockedForLife;

  const billedNote = createMemo<string | null>(() => {
    const p = pricing();
    if (!p) return null;
    if (props.interval === "yearly") {
      const total = p.cycleTotalPromo ?? p.annualTotal ?? p.cycleTotal;
      return hasPromo()
        ? t("billing.sales.annual_total_locked", { total: formatMoney(total) })
        : t("billing.sales.annual_total", { total: formatMoney(total) });
    }
    return hasPromo() ? t("billing.sales.billed_monthly") : null;
  });

  // Backend-derived fallback bullets (used when the parent doesn't supply curated copy).
  const backendFeatureLines = createMemo<string[]>(() => {
    const f = props.plan.features;
    if (!f) return [];
    const lines: string[] = [];

    if (f.max_team_members === -1) {
      lines.push(t("billing.sales.features.members_unlimited"));
    } else if (f.max_team_members === 1) {
      lines.push(t("billing.sales.features.members_one"));
    } else {
      lines.push(
        t("billing.sales.features.members_other", {
          count: String(f.max_team_members),
        }),
      );
    }

    if (f.max_active_job_posts === -1) {
      lines.push(t("billing.sales.features.active_offers_unlimited"));
    } else {
      lines.push(
        t("billing.sales.features.active_offers", {
          count: String(f.max_active_job_posts),
        }),
      );
    }

    if (f.ai_matching === "basic") {
      lines.push(t("billing.sales.features.ai_matching_basic"));
    } else if (f.ai_matching === true) {
      lines.push(t("billing.sales.features.ai_matching"));
    }

    if (f.ats) lines.push(t("billing.sales.features.ats"));

    if (f.support_level === "priority") {
      lines.push(t("billing.sales.features.support_priority"));
    } else if (f.support_level === "dedicated") {
      lines.push(t("billing.sales.features.support_dedicated"));
    } else if (f.support_level === "email") {
      lines.push(t("billing.sales.features.support_email"));
    }

    return lines;
  });

  const featureLines = createMemo<string[]>(() =>
    props.features && props.features.length > 0
      ? props.features
      : backendFeatureLines(),
  );

  // Trial line under the CTA. Monthly always trials (30 days); yearly only while
  // the "free until 1 Jan" launch promo is live. The price shown is the amount
  // actually charged after the free period (promo-adjusted when a discount applies).
  const trialNote = createMemo<string | null>(() => {
    if (!props.showTrialNote) return null;
    const p = pricing();
    if (!p) return null;
    if (props.interval === "yearly") {
      if (!getCheckoutTrialInfo("yearly").isTrial) return null;
      const annual = p.cycleTotalPromo ?? p.annualTotal ?? p.cycleTotal;
      return t("billing.sales.trial_note_yearly", {
        price: formatMoney(annual),
      });
    }
    const monthly = p.promo ? (p.promo?.promo ?? 0) : p.monthlyEquivalent;
    return t("billing.sales.trial_note_monthly", {
      price: formatMoney(monthly),
    });
  });

  const ctaNote = createMemo<string | null>(
    () => trialNote() ?? props.ctaNote ?? null,
  );

  const priceColor = () =>
    props.recommended ? "text-primary" : "text-foreground";
  const iconBg = () =>
    props.recommended ? "bg-primary/10" : "bg-[#100a1b]/[0.07]";

  return (
    <div
      class={cn(
        "flex flex-col justify-between rounded-[18px] border border-border bg-white p-[26px] transition-[box-shadow,border-color,translate] duration-200 hover:-translate-y-[3px]",
        props.currentBadge
          ? "border-primary ring-2 ring-primary/20"
          : props.recommended
            ? "border-primary/35 shadow-[0_2px_10px_-4px_rgba(31,119,235,0.18)] hover:shadow-[0_16px_34px_-16px_rgba(31,119,235,0.4)]"
            : "shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_16px_30px_-16px_rgba(0,0,0,0.2)]",
      )}
    >
      <div>
        {/* header: name + badge + icon */}
        <div class="flex items-start justify-between gap-3">
          <div>
            <div class="flex items-center gap-[9px]">
              <h3 class="font-display text-[22px] font-semibold text-foreground">
                {props.plan.name}
              </h3>
              <Show when={props.currentBadge}>
                <span class="rounded-md bg-primary px-2 py-[3px] text-[10.5px] font-bold uppercase tracking-[0.04em] text-primary-foreground">
                  {t("billing.upgrade.current_plan_badge")}
                </span>
              </Show>
              <Show when={props.recommended && !props.currentBadge}>
                <span class="rounded-md bg-primary/10 px-2 py-[3px] text-[10.5px] font-bold uppercase tracking-[0.04em] text-primary">
                  {t("billing.sales.most_popular_badge")}
                </span>
              </Show>
            </div>
            <Show when={props.plan.description}>
              {(desc) => (
                <p class="mt-[5px] text-[13.5px] text-muted-foreground">
                  {desc()}
                </p>
              )}
            </Show>
          </div>
          <Show when={props.icon}>
            <div
              class={cn(
                "inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full",
                iconBg(),
              )}
            >
              {props.icon}
            </div>
          </Show>
        </div>

        {/* price */}
        <Show when={!isContactSales() && pricing()}>
          {(p) => (
            <>
              <div class="mt-[18px] flex items-end gap-[9px]">
                <div
                  class={cn(
                    "font-display text-[46px] font-semibold leading-[0.9] tracking-[-0.02em]",
                    priceColor(),
                  )}
                >
                  €
                  {formatMoney(
                    p().promo ? (p().promo?.promo ?? 0) : p().monthlyEquivalent,
                  )}
                </div>
                <div class="pb-2 text-sm leading-[1.25] text-muted-foreground">
                  {t("billing.sales.per_month")}
                  <Show when={props.priceSubtext}>
                    {(sub) => (
                      <>
                        <br />
                        <span class="text-xs">{sub()}</span>
                      </>
                    )}
                  </Show>
                </div>
              </div>

              <Show when={p().promo}>
                {(promo) => (
                  <div class="mt-[7px] flex items-center gap-[9px]">
                    <span class="text-sm text-muted-foreground line-through">
                      €{formatMoney(promo().strike)}
                    </span>
                    <span class="rounded-md bg-[#100a1b] px-2 py-[3px] text-[11px] font-bold tracking-[0.03em] text-[#ffd62c]">
                      {t("billing.sales.early_adopter_badge", {
                        percent: String(props.discountPercent ?? 0),
                      })}
                    </span>
                  </div>
                )}
              </Show>

              <Show when={billedNote()}>
                {(note) => (
                  <p class="mt-2 text-[12.5px] text-muted-foreground">
                    {note()}
                  </p>
                )}
              </Show>

              <Show when={props.tag}>
                {(tag) => (
                  <div class="mt-[11px] inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-[11px] py-1.5 text-[12.5px] font-semibold text-primary">
                    <BiSolidTag class="h-[14px] w-[14px]" />
                    {tag()}
                  </div>
                )}
              </Show>
            </>
          )}
        </Show>

        {/* divider */}
        <div class="my-5 h-px bg-border" />

        {/* features */}
        <ul class="flex flex-col gap-[13px]">
          <For each={featureLines()}>
            {(line) => (
              <li class="flex items-start gap-2.5 text-sm leading-[1.35] text-[hsl(222.2_40%_22%)]">
                <BiSolidCheckCircle class="mt-px h-[18px] w-[18px] shrink-0 text-primary" />
                <span>{line}</span>
              </li>
            )}
          </For>
        </ul>
      </div>

      {/* CTA */}
      <div class="mt-6">
        <Button
          class="h-12 w-full rounded-[11px] text-[15px] font-semibold"
          variant={props.cta.variant ?? "default"}
          disabled={props.cta.disabled}
          onClick={() => props.cta.onClick?.()}
        >
          {props.cta.label}
        </Button>
        <Show when={ctaNote()}>
          {(note) => (
            <p class="mt-2.5 text-center text-xs text-muted-foreground">
              {note()}
            </p>
          )}
        </Show>
      </div>
    </div>
  );
};
